import { useState, useEffect, useMemo, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useToast } from '../context/ToastContext';
import { useConfirm } from '../context/ConfirmContext';
import { useFiscalYear } from '../context/FiscalYearContext';
import axios from 'axios';
import logger from '../utils/logger';
import { API_URL } from '../utils/apiConfig';
import { optimisticSave } from '../utils/optimisticSave';

const useDashboard = () => {
  const { t, language } = useLanguage();
  const { showError } = useToast();
  const { confirm } = useConfirm();
  const { fiscalYears, currentFiscalYear } = useFiscalYear();

  // Tab
  const [activeTab, setActiveTab] = useState('suivi');

  // Data
  const [summary, setSummary] = useState(null);
  const [reportingView, setReportingView] = useState('market');
  // FEAT-01d : toggle "masquer événements extraordinaires" (Q2 : reset session,
  // useState PAS localStorage → défaut "tout affiché" revient chaque session).
  // 🚨 RF1 : filtre la VUE de pilotage uniquement, le bilan GHG officiel + les
  // exports restent exhaustifs (calculate_scope_emissions intacte côté backend).
  const [excludeExtraordinary, setExcludeExtraordinary] = useState(false);
  const [kpis, setKpis] = useState(null);
  const [fiscalComparison, setFiscalComparison] = useState([]);
  const [scopeBreakdown, setScopeBreakdown] = useState(null);
  const [selectedFiscalYearForChart, setSelectedFiscalYearForChart] = useState('current');
  const [drillDownScope, setDrillDownScope] = useState(null);
  const [loading, setLoading] = useState(true);

  // LOT 6C : states locaux pour le useEffect derive setStats (view-INDEP fetches)
  const [activitiesData, setActivitiesData] = useState(null);
  const [products, setProducts] = useState(null);

  const [stats, setStats] = useState({
    totalActivities: 0,
    totalProducts: 0,
    totalEmissions: 0,
    completedCategories: 0,
    totalCategories: 0
  });

  // Scenarios
  const [selectedScenarioEntityId, setSelectedScenarioEntityId] = useState(null);
  const [scenarioEntities, setScenarioEntities] = useState([]);
  const [scenarioDataPoints, setScenarioDataPoints] = useState([]);
  const [scenarioSummary, setScenarioSummary] = useState(null);

  // Objectives
  const [objective, setObjective] = useState(null);
  const [trajectoryData, setTrajectoryData] = useState({ trajectory: [], actuals: [] });

  // Plausibility
  const [plausibilityResult, setPlausibilityResult] = useState(null);
  const [plausibilityLoading, setPlausibilityLoading] = useState(false);
  // ENH-04 Lot 3b : code catégorie en cours de toggle (anti double-clic + spinner).
  const [togglingCode, setTogglingCode] = useState(null);
  const [showObjectiveModal, setShowObjectiveModal] = useState(false);
  const [objectiveForm, setObjectiveForm] = useState({
    reference_fiscal_year_id: '',
    target_year: 2030,
    // FEAT-01h : toggle exclure les événements extraordinaires du calcul objectif.
    // Défaut False (inclut — rétrocompat backend axe 6).
    exclude_extraordinary: false,
    // FIX #1 Temps B : objectif porte sa méthode reporting_view (LB/MB).
    // Défaut 'market' = invariant rétro-compat baseline.
    reporting_view: 'market',
  });
  const [objectiveLoading, setObjectiveLoading] = useState(false);
  // FEAT-01h : le FY de référence sélectionné dans la modale objectif a-t-il au
  // moins 1 activité extraordinaire ? Pilote l'affichage conditionnel de la
  // commande exclude_extraordinary (Q_FEAT.39 A). Détection via endpoint EXISTANT
  // /api/activities?fiscal_year_id= (0 nouvel endpoint) car le FY de référence est
  // arbitraire (≠ FY courant) donc pas dans activitiesData déjà chargé.
  const [refFyHasExtraordinary, setRefFyHasExtraordinary] = useState(false);

  // FEAT-01g #2 : détection Approche 1 — le FY courant a-t-il au moins 1 activité
  // extraordinaire ? Déduit de activitiesData déjà chargé (GET /activities du FY,
  // is_extraordinary sérialisé). Pilote l'affichage conditionnel du toggle dashboard
  // (contrôle de filtrage masqué si rien à filtrer). 🚨 RF1 inchangé (pur affichage).
  const hasExtraordinary = useMemo(
    () => Array.isArray(activitiesData) && activitiesData.some(a => a.is_extraordinary === true),
    [activitiesData]
  );

  // Scope names (depends on t)
  const scopeNames = useMemo(() => ({
    scope1: t('dashboard.scopes.scope1'),
    scope2: t('dashboard.scopes.scope2'),
    scope3_amont: t('dashboard.scopes.scope3_amont'),
    scope3_aval: t('dashboard.scopes.scope3_aval'),
  }), [t]);

  // ─── Data fetching (LOT 6C : split fan-out toggle) ───

  // view-INDEP + FY-INDEP : 3 endpoints fetched once at mount (company scope)
  const fetchCompanyScope = useCallback(async () => {
    try {
      const [productsRes, objRes, scenariosRes] = await Promise.all([
        axios.get(`${API_URL}/api/products`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/objectives`).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/scenarios`).catch(() => ({ data: [] })),
      ]);
      setProducts(productsRes.data || []);
      setObjective(objRes.data);
      setScenarioEntities(scenariosRes.data || []);
    } catch (error) {
      logger.error('Failed to fetch company-scope data:', error);
    }
  }, []);

  // view-INDEP + FY-DEP : 1 endpoint (activities) fetched on FY change only
  const fetchActivitiesForFY = useCallback(async () => {
    try {
      const fyParamAmp = currentFiscalYear?.id ? `&fiscal_year_id=${currentFiscalYear.id}` : '';
      const activitiesRes = await axios.get(
        `${API_URL}/api/activities?limit=100${fyParamAmp}`
      ).catch(() => ({ data: { data: [] } }));
      const data = activitiesRes.data?.data || activitiesRes.data || [];
      setActivitiesData(data);
    } catch (error) {
      logger.error('Failed to fetch activities:', error);
    }
  }, [currentFiscalYear?.id]);

  // view-DEP : 4 endpoints (summary, kpis, fiscal-comparison, scope-breakdown)
  const fetchViewDependentDashboard = useCallback(async () => {
    try {
      const fyParam = currentFiscalYear?.id ? `?fiscal_year_id=${currentFiscalYear.id}` : '';
      const rvParam = reportingView === 'location' ? `${fyParam ? '&' : '?'}reporting_view=location` : '';
      const rvParamOnly = reportingView === 'location' ? '?reporting_view=location' : '';
      // FEAT-01d : param exclude_extraordinary passé aux routes FEAT-01 filtrées
      // (summary + fiscal-comparison + scope-breakdown). kpis n'est PAS filtrée
      // (hors périmètre FEAT-01c/c'/c''). 🚨 RF1 : vue pilotage uniquement.
      const ee = excludeExtraordinary;
      const eeWith = (prefixHasQuery) => ee ? `${prefixHasQuery ? '&' : '?'}exclude_extraordinary=true` : '';

      const [summaryRes, kpisRes, comparisonRes, breakdownRes] = await Promise.all([
        axios.get(`${API_URL}/api/dashboard/summary${fyParam}${rvParam}${eeWith(!!fyParam || reportingView === 'location')}`),
        axios.get(`${API_URL}/api/dashboard/kpis${fyParam}${rvParam}`),
        axios.get(`${API_URL}/api/dashboard/fiscal-comparison${rvParamOnly}${eeWith(reportingView === 'location')}`),
        axios.get(`${API_URL}/api/dashboard/scope-breakdown/${currentFiscalYear?.id || 'current'}${rvParamOnly}${eeWith(reportingView === 'location')}`),
      ]);

      setSummary(summaryRes.data);
      setKpis(kpisRes.data);
      setFiscalComparison(comparisonRes.data);
      setScopeBreakdown(breakdownRes.data);
    } catch (error) {
      logger.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [currentFiscalYear?.id, reportingView, excludeExtraordinary]);

  // view-DEP + FY-INDEP : 1 endpoint (objectives/trajectory)
  // FEAT-01i : passe exclude_extraordinary (toggle dashboard) → filtre SEULEMENT
  // les barres actuals (réalité brute). baseline/cible figées suivent l'objectif.
  const fetchTrajectory = useCallback(async () => {
    try {
      const rvParam = reportingView === 'location' ? '?reporting_view=location' : '';
      const eeParam = excludeExtraordinary ? `${rvParam ? '&' : '?'}exclude_extraordinary=true` : '';
      const trajRes = await axios.get(
        `${API_URL}/api/objectives/trajectory${rvParam}${eeParam}`
      ).catch(() => ({ data: { trajectory: [], actuals: [] } }));
      setTrajectoryData(trajRes.data);
    } catch (error) {
      logger.error('Failed to fetch trajectory:', error);
    }
  }, [reportingView, excludeExtraordinary]);

  // Refetch full objectives state (used by handleSubmitObjective after POST/PUT)
  const fetchObjectiveData = useCallback(async () => {
    try {
      const rvParam = reportingView === 'location' ? '?reporting_view=location' : '';
      // FEAT-01i : trajectory actuals suivent le toggle dashboard (réalité brute).
      const eeParam = excludeExtraordinary ? `${rvParam ? '&' : '?'}exclude_extraordinary=true` : '';
      const [objRes, trajRes] = await Promise.all([
        axios.get(`${API_URL}/api/objectives`).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/objectives/trajectory${rvParam}${eeParam}`).catch(() => ({ data: { trajectory: [], actuals: [] } })),
      ]);
      setObjective(objRes.data);
      setTrajectoryData(trajRes.data);
    } catch (error) {
      logger.error('Failed to fetch objective data:', error);
    }
  }, [reportingView, excludeExtraordinary]);

  const fetchScopeBreakdown = useCallback(async (fyId) => {
    try {
      const rvParam = reportingView === 'location' ? '?reporting_view=location' : '';
      // FEAT-01d : drill-down cohérent avec le toggle dashboard (vue pilotage).
      const eeParam = excludeExtraordinary ? `${rvParam ? '&' : '?'}exclude_extraordinary=true` : '';
      const response = await axios.get(`${API_URL}/api/dashboard/scope-breakdown/${fyId}${rvParam}${eeParam}`);
      const data = response.data;

      const scopeLabels = {
        scope1: t('dashboard.scopes.scope1'),
        scope2: t('dashboard.scopes.scope2'),
        scope3_amont: t('dashboard.scopes.scope3_amont'),
        scope3_aval: t('dashboard.scopes.scope3_aval')
      };

      const scope_data = Object.entries(data.scopes || {}).map(([scope, values]) => ({
        name: scopeLabels[scope] || scope,
        scope,
        emissions: values.total || 0,
        categories: values.categories || {}
      })).filter(s => s.emissions > 0);

      setScopeBreakdown({ ...data, scope_data });
      setDrillDownScope(null);
    } catch (error) {
      logger.error('Failed to fetch scope breakdown:', error);
    }
  }, [reportingView, t, excludeExtraordinary]);

  // ─── Effects (LOT 6C : split fan-out) ───

  // useEffect A — mount only (view-INDEP + FY-INDEP)
  useEffect(() => {
    fetchCompanyScope();
  }, [fetchCompanyScope]);

  // useEffect B — FY change only (view-INDEP + FY-DEP)
  useEffect(() => {
    fetchActivitiesForFY();
  }, [fetchActivitiesForFY]);

  // useEffect C — FY OR view change (view-DEP)
  // Inclut le reset des states scenario/plausibility (deplace depuis l'ancien
  // useEffect principal pour preserver le comportement metier).
  useEffect(() => {
    fetchViewDependentDashboard();
    fetchTrajectory();
    if (currentFiscalYear?.id) {
      setSelectedFiscalYearForChart(currentFiscalYear.id);
    }
    setSelectedScenarioEntityId(null);
    setScenarioDataPoints([]);
    setScenarioSummary(null);
    setPlausibilityResult(null);
  }, [fetchViewDependentDashboard, fetchTrajectory, currentFiscalYear?.id]);

  // useEffect D (derived) — recompose stats dès que les 3 sources sont prêtes.
  // Pattern React-idiomatic : pas de bug de race, fallback graceful via
  // `summary?.activities_count || activitiesData?.length || 0`.
  useEffect(() => {
    if (summary === null && activitiesData === null && products === null) return;
    const scopeCompletion = summary?.scope_completion || {};
    let completedCats = 0;
    let totalCats = 0;
    Object.values(scopeCompletion).forEach(scope => {
      completedCats += scope.completed || 0;
      totalCats += scope.total_categories || 0;
    });
    setStats({
      totalActivities: summary?.activities_count ?? (activitiesData?.length || 0),
      totalProducts: summary?.products_count ?? (products?.length || 0),
      totalEmissions: summary?.total_emissions || 0,
      completedCategories: completedCats,
      totalCategories: totalCats,
    });
  }, [summary, activitiesData, products]);

  useEffect(() => {
    if (selectedFiscalYearForChart && selectedFiscalYearForChart !== 'current') {
      fetchScopeBreakdown(selectedFiscalYearForChart);
    }
  }, [selectedFiscalYearForChart, reportingView, fetchScopeBreakdown]);

  // FEAT-01h : détection extraordinaire sur le FY de référence de la modale objectif.
  // No-op si aucun FY sélectionné (refId vide → 0 fetch, préserve le compteur de
  // fetches au mount). Réutilise l'endpoint EXISTANT /api/activities (0 nouvel
  // endpoint). 🚨 RF1 inchangé (détection d'affichage, ne touche aucun calcul).
  useEffect(() => {
    const refId = objectiveForm.reference_fiscal_year_id;
    if (!refId) { setRefFyHasExtraordinary(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/activities?limit=500&fiscal_year_id=${refId}`);
        const data = res.data?.data || res.data || [];
        if (!cancelled) setRefFyHasExtraordinary(data.some(a => a.is_extraordinary === true));
      } catch (error) {
        logger.error('Failed to detect extraordinary on reference FY:', error);
        if (!cancelled) setRefFyHasExtraordinary(false);
      }
    })();
    return () => { cancelled = true; };
  }, [objectiveForm.reference_fiscal_year_id]);

  // Fetch all periods for selected scenario entity
  useEffect(() => {
    if (!selectedScenarioEntityId) {
      setScenarioDataPoints([]);
      setScenarioSummary(null);
      return;
    }
    const fetchAllScenarioPeriods = async () => {
      try {
        const fyRes = await axios.get(`${API_URL}/api/fiscal-years`);
        const allFys = fyRes.data || [];
        const scenarioFys = allFys.filter(fy => fy.scenario_id === selectedScenarioEntityId && fy.type === 'scenario');

        if (scenarioFys.length === 0) {
          setScenarioDataPoints([]);
          setScenarioSummary(null);
          return;
        }

        const summaries = await Promise.all(
          scenarioFys.map(fy =>
            axios.get(`${API_URL}/api/dashboard/summary?fiscal_year_id=${fy.id}`)
              .then(r => ({ year: fy.year, data: r.data, fyId: fy.id }))
              .catch(() => null)
          )
        );

        const dataPoints = summaries.filter(Boolean).map(s => {
          const se = s.data?.scope_emissions || {};
          return {
            year: s.year,
            scope1: se.scope1 || 0,
            scope2: se.scope2 || 0,
            scope3: (se.scope3_amont || 0) + (se.scope3_aval || 0)
          };
        });

        setScenarioDataPoints(dataPoints);

        const latestPeriod = summaries.filter(Boolean).sort((a, b) => b.year - a.year)[0];
        if (latestPeriod) {
          setScenarioSummary({ summary: latestPeriod.data });
        }
      } catch (error) {
        logger.error('Failed to fetch scenario periods:', error);
        setScenarioDataPoints([]);
        setScenarioSummary(null);
      }
    };
    fetchAllScenarioPeriods();
  }, [selectedScenarioEntityId]);

  // ─── Handlers ───

  // LOT D — la locale ACTIVE de l'UI est transmise au BE (qui traduit les
  // messages). Indispensable : LanguageContext vit dans localStorage et n'est
  // jamais synchronisé vers `user.language` → le fallback profil seul
  // renverrait du FR sous une UI DE.
  // `language` en dépendance = re-fetch au basculement de langue : TrackingTab
  // dédup son auto-run par IDENTITÉ de ce callback → nouvelle identité →
  // nouveau check → messages retraduits. Aucun effet supplémentaire à écrire.
  const runPlausibilityCheck = useCallback(async () => {
    setPlausibilityLoading(true);
    setPlausibilityResult(null);
    try {
      const params = new URLSearchParams({ language });
      if (currentFiscalYear?.id) params.set('fiscal_year_id', currentFiscalYear.id);
      const response = await axios.post(`${API_URL}/api/plausibility/check?${params.toString()}`);
      setPlausibilityResult(response.data);
    } catch (error) {
      logger.error('Failed to run plausibility check:', error);
    } finally {
      setPlausibilityLoading(false);
    }
  }, [currentFiscalYear?.id, language]);

  // P3 L2b — dismiss/restore d'une alerte (FY courant, rule_id) puis refresh.
  // BE : POST/DELETE /api/plausibility/dismiss (garde require_fy_modify_state,
  // le FE gate l'UI via isCsAdmin/isCompanyAdmin — même logique que close).
  // No-op sans FY courant (le dismiss est par (FY, rule_id)).
  const dismissPlausibilityAlert = useCallback(async (ruleId) => {
    if (!currentFiscalYear?.id) return;
    try {
      await axios.post(`${API_URL}/api/plausibility/dismiss`, {
        fiscal_year_id: currentFiscalYear.id, rule_id: ruleId,
      });
      await runPlausibilityCheck();
    } catch (error) {
      logger.error('Failed to dismiss plausibility alert:', error);
    }
  }, [currentFiscalYear?.id, runPlausibilityCheck]);

  const restorePlausibilityAlert = useCallback(async (ruleId) => {
    if (!currentFiscalYear?.id) return;
    try {
      await axios.delete(
        `${API_URL}/api/plausibility/dismiss?fiscal_year_id=${currentFiscalYear.id}&rule_id=${ruleId}`
      );
      await runPlausibilityCheck();
    } catch (error) {
      logger.error('Failed to restore plausibility alert:', error);
    }
  }, [currentFiscalYear?.id, runPlausibilityCheck]);

  // ENH-04 Lot 3b : toggle "terminée" d'une catégorie. PUT /context PARTIEL
  // (completed_categories seul, sans updated_at — cf. FIX 3b) via optimisticSave
  // (409 résiduel → toast central), puis refetch du dashboard (jauges à jour).
  const toggleCategoryCompleted = useCallback(async (code) => {
    if (!currentFiscalYear?.id) return;
    setTogglingCode(code);
    try {
      const current = summary?.completed_categories || [];
      const newList = current.includes(code)
        ? current.filter((c) => c !== code)
        : [...current, code];
      await optimisticSave(t, () =>
        // FIX 3b : pas d'updated_at — le sentinel optimiste BE est skippé quand
        // absent (optimistic_concurrency.py:69). Le toggle de complétion est une
        // action légère mono-utilisateur ; sans ça, le 2e toggle enverrait un
        // updated_at périmé (currentFiscalYear pas rafraîchi) → 409 parasite.
        axios.put(`${API_URL}/api/fiscal-years/${currentFiscalYear.id}/context`, {
          completed_categories: newList,
        })
      );
      // Refetch dans tous les cas : succès (état à jour) OU conflit 409
      // (optimisticSave a déjà toasté ; refetch restaure la vérité + updated_at).
      await fetchViewDependentDashboard();
    } catch (error) {
      logger.error('Failed to toggle category completion:', error);
      showError(t('dashboard.alerts.saveError').replace('{detail}', error.response?.data?.detail || error.message));
    } finally {
      setTogglingCode(null);
    }
  }, [currentFiscalYear, summary, t, fetchViewDependentDashboard, showError]);

  // FEAT-01h : création (POST) OU édition (PUT) selon le contexte.
  //   - objectif existant ET même FY de référence → PUT /objectives/{id}
  //     (recalcul baseline immédiat backend, pas d'archivage : recalcule en place).
  //   - sinon (pas d'objectif, OU changement de FY de référence) → POST
  //     (création ou re-création avec nouvelle baseline FY — capacité préservée).
  // exclude_extraordinary transite dans les deux payloads (POST + PUT).
  const handleSubmitObjective = useCallback(async (customTargets = {}) => {
    if (!objectiveForm.reference_fiscal_year_id) {
      showError(t('dashboard.alerts.referenceYearRequired'));
      return;
    }
    setObjectiveLoading(true);
    try {
      const isInPlaceEdit = objective?.id
        && objectiveForm.reference_fiscal_year_id === objective.reference_fiscal_year_id;
      if (isInPlaceEdit) {
        await axios.put(`${API_URL}/api/objectives/${objective.id}`, {
          target_year: objectiveForm.target_year,
          exclude_extraordinary: objectiveForm.exclude_extraordinary,
          // FIX #1 Temps B : reporting_view modifiable → BE détecte le changement
          // et recompute la baseline (pattern needs_recompute étendu).
          reporting_view: objectiveForm.reporting_view,
        });
      } else {
        // FEAT-02 : customTargets = { custom_reduction_scope1_2_percent?,
        // custom_reduction_scope3_percent? } injectés UNIQUEMENT si le mode avancé
        // a été utilisé. Vide ({}) par défaut → POST inchangé (chemin SBTi).
        await axios.post(`${API_URL}/api/objectives`, { ...objectiveForm, ...customTargets });
      }
      await fetchObjectiveData();
      setShowObjectiveModal(false);
    } catch (error) {
      logger.error('Failed to save objective:', error);
      showError(t('dashboard.alerts.saveError').replace('{detail}', error.response?.data?.detail || error.message));
    } finally {
      setObjectiveLoading(false);
    }
  }, [objectiveForm, objective, fetchObjectiveData]);

  const handleArchiveObjective = useCallback(async () => {
    if (!objective?.id) return;
    if (!await confirm({ message: t('dashboard.alerts.archiveConfirm'), variant: 'warning' })) return;
    try {
      await axios.delete(`${API_URL}/api/objectives/${objective.id}`);
      setObjective(null);
      setTrajectoryData({ trajectory: [], actuals: [] });
    } catch (error) {
      logger.error('Failed to archive objective:', error);
      showError(t('dashboard.alerts.archiveError').replace('{detail}', error.response?.data?.detail || error.message));
    }
  }, [objective?.id]);

  return {
    fiscalYears, currentFiscalYear,
    activeTab, setActiveTab,
    summary, reportingView, setReportingView,
    excludeExtraordinary, setExcludeExtraordinary,
    hasExtraordinary,
    kpis, fiscalComparison, scopeBreakdown,
    loading, stats, scopeNames,
    plausibilityResult, plausibilityLoading, runPlausibilityCheck,
    dismissPlausibilityAlert, restorePlausibilityAlert,
    toggleCategoryCompleted, togglingCode,
    objective, trajectoryData,
    showObjectiveModal, setShowObjectiveModal,
    objectiveForm, setObjectiveForm, objectiveLoading,
    refFyHasExtraordinary,
    handleSubmitObjective, handleArchiveObjective,
    selectedScenarioEntityId, setSelectedScenarioEntityId,
    scenarioEntities, scenarioDataPoints, scenarioSummary,
  };
};

export default useDashboard;
